import { useState, useEffect } from 'react';
import { useAuth } from '../auth/useAuth';
import { supabase } from '../../lib/supabase';
import { toast } from 'react-hot-toast';
import { isNetworkError } from '../../utils/api/networkErrors';

const MAX_RETRIES = 3;
const RETRY_DELAY = 1000;

export function useAdmin() {
  const [isAdmin, setIsAdmin] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const { user } = useAuth();

  useEffect(() => {
    let mounted = true;
    let retryCount = 0;

    const checkAdminStatus = async () => {
      try {
        if (!user) {
          if (mounted) {
            setIsAdmin(false);
            setIsLoading(false);
          }
          return;
        }

        // Special case for known admin email
        if (user.email === 'theresa@tezhly.com') {
          if (mounted) {
            setIsAdmin(true);
            setIsLoading(false);
          }
          return;
        }

        // Try up to MAX_RETRIES times with exponential backoff
        while (retryCount < MAX_RETRIES) {
          try {
            const { data: profile, error } = await supabase
              .from('user_profiles')
              .select('is_admin')
              .eq('id', user.id)
              .maybeSingle();

            if (error) {
              if (error.code === 'PGRST116' || isNetworkError(error)) {
                retryCount++;
                if (retryCount < MAX_RETRIES) {
                  await new Promise(resolve => setTimeout(resolve, RETRY_DELAY * Math.pow(2, retryCount)));
                  continue;
                }
              }
              throw error;
            }

            if (mounted) {
              // Check all admin conditions
              const isAdminUser = 
                !!profile?.is_admin || 
                user.email === 'theresa@tezhly.com' ||
                user.app_metadata?.role === 'admin';

              setIsAdmin(isAdminUser);
              setIsLoading(false);
            }
            return;
          } catch (retryError) {
            if (retryCount >= MAX_RETRIES - 1) throw retryError;
            retryCount++;
            await new Promise(resolve => setTimeout(resolve, RETRY_DELAY * Math.pow(2, retryCount)));
          }
        }
      } catch (error) {
        console.error('Error checking admin status:', error);
        if (mounted) {
          // Fallback to email check if database query fails
          setIsAdmin(user.email === 'theresa@tezhly.com');
          setIsLoading(false);
        }
      }
    };

    checkAdminStatus();

    return () => {
      mounted = false;
    };
  }, [user]);

  return { isAdmin, isLoading };
}